import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Send, MessageSquare } from "lucide-react";

export default function OrderMessages({ orderId, role, compact = false, autoMarkRead = true, onUnreadChange }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const endRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/orders/${orderId}/messages`);
      setMessages(data.messages || []);
      if (onUnreadChange) onUnreadChange(data.unread || 0);
      if (autoMarkRead && (data.unread || 0) > 0) {
        await api.patch(`/orders/${orderId}/messages/read`);
        if (onUnreadChange) onUnreadChange(0);
      }
      setLoaded(true);
    } catch {
      setLoaded(true);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  useEffect(() => {
    if (endRef.current) endRef.current.scrollTop = endRef.current.scrollHeight;
  }, [messages.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      const { data } = await api.post(`/orders/${orderId}/messages`, { message: text });
      setMessages((m) => [...m, data]);
      setText("");
    } finally {
      setSending(false);
    }
  };

  const fmtTime = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return ""; }
  };

  return (
    <div className="border border-[#e2e8f0] rounded-lg bg-white overflow-hidden" data-testid={`messages-${orderId}`}>
      <div className="px-4 py-3 border-b border-[#e2e8f0] bg-[#f8fafc] flex items-center gap-2">
        <MessageSquare className="w-4 h-4 text-[#2563eb]" />
        <div className="text-sm font-semibold text-[#0f1b2d]">Messages</div>
      </div>
      <div ref={endRef} className={`px-4 py-3 ${compact ? "max-h-56" : "max-h-72"} overflow-y-auto space-y-3`}>
        {!loaded ? (
          <div className="text-sm text-[#475569]">Loading…</div>
        ) : messages.length === 0 ? (
          <div className="text-sm text-[#475569] text-center py-6">No messages yet. Start the conversation.</div>
        ) : (
          messages.map((m) => {
            const mine = m.sender_role === role;
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${mine ? "bg-[#2563eb] text-white" : "bg-[#f1f5f9] text-[#0f1b2d]"}`}>
                  <div className={`text-[10px] uppercase tracking-wider mb-1 ${mine ? "text-white/70" : "text-[#64748b]"}`}>
                    {m.sender_role === "admin" ? "Admin" : m.sender_name || "You"} · {fmtTime(m.created_at)}
                  </div>
                  <div className="whitespace-pre-wrap break-words">{m.message}</div>
                </div>
              </div>
            );
          })
        )}
      </div>
      <form onSubmit={send} className="border-t border-[#e2e8f0] p-3 flex items-center gap-2">
        <input
          data-testid={`message-input-${orderId}`}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Type a message…"
          className="flex-1 border border-[#e2e8f0] rounded-md px-3 py-2 text-sm outline-none focus:border-[#2563eb]"
        />
        <button
          type="submit"
          disabled={sending || !text.trim()}
          data-testid={`message-send-${orderId}`}
          className="inline-flex items-center gap-1.5 bg-[#2563eb] hover:bg-[#1d4ed8] disabled:opacity-60 text-white text-sm font-semibold px-3 py-2 rounded-md"
        >
          <Send className="w-4 h-4" /> Send
        </button>
      </form>
    </div>
  );
}
